import { memo } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, LabelList, Cell,
} from 'recharts';
import { ChartWrapper } from '../shared/ChartWrapper';
import { NoChartData } from '../shared/NoChartData';
import { usePortfolioData } from './usePortfolioData';

const LABEL_STYLE = { fontSize: 11, fill: '#666' };

const COLORS = {
  Principal: '#2d7f3f',
  Significant: '#3B9B61',
};

const IA_COLORS = {
  'Gender': '#009688',
  'Climate': '#2196F3',
  'Nutrition': '#FF9800',
  'Biodiversity': '#4CAF50',
  'Poverty': '#9C27B0',
};

function getIAColor(name) {
  for (const [key, color] of Object.entries(IA_COLORS)) {
    if (name && name.toLowerCase().includes(key.toLowerCase())) return color;
  }
  return '#2d7f3f'; // fallback
}

function lightenHex(hex, amount) {
  const num = parseInt(hex.replace('#', ''), 16);
  const r = Math.round(((num >> 16) & 0xff) + (255 - ((num >> 16) & 0xff)) * amount);
  const g = Math.round(((num >> 8) & 0xff) + (255 - ((num >> 8) & 0xff)) * amount);
  const b = Math.round((num & 0xff) + (255 - (num & 0xff)) * amount);
  return `rgb(${r},${g},${b})`;
}

/**
 * ImpactAreaSummaryChart
 * Stacked bar chart showing how many portfolio results target each of the five
 * CGIAR Impact Areas at Principal or Significant level.
 */
export const ImpactAreaSummaryChart = memo(function ImpactAreaSummaryChart({ data: dataProp }) {
  const { impactAreaBreakdown, filteredResults } = usePortfolioData();

  const data = dataProp ?? impactAreaBreakdown;

  if (!data || data.length === 0 || data.every(d => d.total === 0)) {
    return (
      <ChartWrapper title="Portfolio Impact Area Coverage">
        <NoChartData
          message="No impact area data available"
          detail="This chart shows how many results across the portfolio are tagged at Principal or Significant level for each of the five CGIAR Impact Areas. No impact area tags were found in the current selection."
        />
      </ChartWrapper>
    );
  }

  const exportData = data.map(d => ({
    'Impact Area': d.name,
    Principal: d.Principal,
    Significant: d.Significant,
    Total: d.total,
  }));

  const renderStackTotal = (props) => {
    const { x, y, width, index } = props;
    const entry = data[index];
    if (!entry || entry.total === 0) return null;
    return (
      <text
        x={x + width / 2}
        y={y - 4}
        {...LABEL_STYLE}
        textAnchor="middle"
      >
        {entry.total}
      </text>
    );
  };

  return (
    <ChartWrapper
      title="Portfolio Impact Area Coverage"
      info="Number of results across all programs targeting each CGIAR Impact Area at Principal or Significant level. A result can count toward multiple Impact Areas."
      chartData={exportData}
      sourceResults={filteredResults}
      exportFilename="impact-area-summary"
    >
      <ResponsiveContainer width="100%" height={320}>
        <BarChart data={data} margin={{ top: 20, right: 20, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 12 }} />
          <Tooltip
            content={({ active, payload, label }) => {
              if (!active || !payload?.length) return null;
              const lookup = {};
              payload.forEach(p => { lookup[p.dataKey] = p.value; });
              const total = (lookup.Principal || 0) + (lookup.Significant || 0);
              return (
                <div style={{
                  background: '#fff', padding: '8px 12px', borderRadius: '8px',
                  border: '1px solid #e5e7eb', boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  fontSize: '13px', lineHeight: 1.6,
                }}>
                  <div style={{ fontWeight: 600, color: '#1e293b', marginBottom: 2 }}>{label}</div>
                  {lookup.Principal !== undefined && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: getIAColor(label), display: 'inline-block' }} />
                      <span style={{ color: '#4b5563' }}>Principal: <strong>{lookup.Principal.toLocaleString()}</strong></span>
                    </div>
                  )}
                  {lookup.Significant !== undefined && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: lightenHex(getIAColor(label), 0.4), display: 'inline-block' }} />
                      <span style={{ color: '#4b5563' }}>Significant: <strong>{lookup.Significant.toLocaleString()}</strong></span>
                    </div>
                  )}
                  <div style={{ marginTop: 4, paddingTop: 4, borderTop: '1px solid #e5e7eb', color: '#6b7280', fontSize: 12 }}>
                    Total targeted: {total.toLocaleString()}
                  </div>
                </div>
              );
            }}
          />
          <Legend
            content={() => (
              <div style={{ display: 'flex', justifyContent: 'center', gap: 16, marginTop: 4 }}>
                {[
                  { label: 'Principal (darker shade)', color: '#374151' },
                  { label: 'Significant (lighter shade)', color: '#6b7280' },
                ].map(({ label, color }) => (
                  <span key={label} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color }}>
                    {label}
                  </span>
                ))}
              </div>
            )}
          />
          <Bar dataKey="Significant" stackId="a">
            {data.map((entry, idx) => (
              <Cell key={idx} fill={lightenHex(getIAColor(entry.name), 0.4)} />
            ))}
          </Bar>
          <Bar dataKey="Principal" stackId="a" radius={[4, 4, 0, 0]}>
            {data.map((entry, idx) => (
              <Cell key={idx} fill={getIAColor(entry.name)} />
            ))}
            <LabelList content={renderStackTotal} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </ChartWrapper>
  );
});
